import _ from 'lodash';
import boolean from 'boolean';
import Debug from '~/utils/debug';
import icons from '~/utils/icons';
import ImageHelper from '~/components/image-helper/ImageHelper.vue';
import LeadFormPreview from '~/components/lead-form-preview/LeadFormPreview.vue';
import OfferFormPreview from '~/components/offer-form-preview/OfferFormPreview.vue';
import { GOAL_LEAD_GENERATION, GOAL_LOCAL_OFFERS, GOAL_TYPES, CAMPAIGN_TYPES, CREATIVE_STATUSES } from '~/utils/constants';
import { mapState, mapActions } from 'vuex';
import Carousel from './carousel/Carousel.vue';
import * as defaults from './defaults';

const debug = Debug('LeadFormPreview'); // eslint-disable-line

const applyKey = (target, source, key, always) => {
  target[key] = always ? source[key] : ((source || {})[key] || target[key]);
};

export default {
  components: {
    Carousel,
    ImageHelper,
    OfferFormPreview,
    'lead-form-preview': LeadFormPreview,
  },
  props: {
    creative: Object,
    facebookPage: Object,
    landingPage: Object,
    disabledButtons: Boolean,
    placeholders: Boolean,
    mobilePreview: {
      type: Boolean,
      default: false,
    },
    showCreativeIdBottom: {
      type: Boolean,
      default: false,
    },
    enableFormPreview: {
      type: Boolean,
      default: false,
    },
    isTimeline: {
      type: Boolean,
      default: false,
    },
    usePortal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      counter: 0,
      timer: null,
      internalMobilePreview: false,
      isHtmlVideoSupported: !!document.createElement('video').canPlayType,
      loading: true,
    };
  },
  methods: {
    ...mapActions('genericConfig', {
      updateGenericConfig: 'updateConfig',
    }),
    isType(...types) {
      return types.includes(this.resultCreative.type);
    },
    onImageLoaded() {
      this.$emit('loaded');
    },
    setMobilePreview(mobile) {
      this.internalMobilePreview = mobile;
      if (this.resultCreative.type === CAMPAIGN_TYPES.CAROUSEL && this.$refs.carousel) {
        this.$refs.carousel.resetCarousel(this.internalMobilePreview);
      }
    },
    showVideo(file) {
      const filename = _.get(file, 'path', '');
      const isVideo = boolean(_.get(file, 'isVideo', false));
      const isVideoFileType = String(_.get(file, 'content_type', '')).startsWith('video/');
      const isSupported = filename && !['avi', 'qt'].includes(filename.split('.').pop());
      return this.isHtmlVideoSupported && (isVideo || isVideoFileType) && isSupported;
    },
    isGif(filename) {
      if (filename) {
        const ext = filename.split('.').pop();
        return ['gif'].includes(ext);
      }
      return false;
    },
    removeEmpty(value) {
      return _.isObject(value)
        ? _(value)
          .omitBy(_.isEmpty)
          .mapValues(v => this.removeEmpty(v))
          .value()
        : value;
    },
    toggleAdFormModal() {
      const form = this.goalType.isOffer ? 'offer' : (this.goalType.isLead ? 'lead' : null); // eslint-disable-line
      if (this.$refs[`${form}-form-modal`]) {
        this.$refs[`${form}-form-modal`].show();
      }
    },
    setMediaValues(res) {
      res.media = this.creative.media.map(media => {
        const defaultValues = defaults.media(this.goalType);
        if (this.inStatus || this.isTimeline || !this.placeholders) {
          delete defaultValues.name;
          delete defaultValues.caption;
          delete defaultValues.description;
        }
        const mediaRes = Object.assign({}, defaultValues);
        defaults.basicMediaKeys.forEach(i => applyKey(mediaRes, media, i, !this.placeholders));
        // load path and use default value if NULL (case for deleted files)
        let pathValue = _.get(media, 'file.path', null);
        if (pathValue === null && this.placeholders) {
          pathValue = mediaRes.path;
        }
        mediaRes.path = pathValue;

        // load thumbail and use default value if NULL (case for deleted files)
        pathValue = _.get(media, 'file.thumbnail', null);
        if (pathValue === null && this.thumbnail) {
          pathValue = mediaRes.thumbnail;
        }
        mediaRes.thumbnail = pathValue;

        mediaRes.content_type = _.get(media, 'file.content_type', '');

        if (!mediaRes.thumbnail && mediaRes.path) {
          // timeline records doesn't have thumbnail
          mediaRes.thumbnail = mediaRes.path;
        }

        if (this.goalType.isOffer) {
          if (_.isEmpty(media.name)) {
            mediaRes.name = res.name;
          }
          mediaRes.description = res.description;
        }
        return mediaRes;
      });
    },
    setOfferConfigValues(res) {
      const offerConfigDefaults = Object.assign({}, defaults.offerConfig());
      // Remove some of default placeholder values as we don't need to show them in preview
      _.forOwn(offerConfigDefaults, (value, key) => {
        if (key === 'code') offerConfigDefaults[key] = null;
      });

      const campaignOfferDetails = _.get(this, 'creative.timeline', [])
        .filter(i => i.type === 'Comment')
        .map(i => i.data)
        .filter(i => i.type === 'brief')
        .map(i => _.get(i, 'details', []).filter(d => d.name === 'offer_config').find(() => true))
        .reverse()
        .find(() => true);
      const creativeOfferDetailsValues = _.get(this.creative, 'details', []).find(d => d.name === 'offer_config');
      const preview = Object.assign(
        {},
        campaignOfferDetails ? campaignOfferDetails.value : {},
        creativeOfferDetailsValues ? creativeOfferDetailsValues.value : {},
      );

      res.offer_config = Object.assign(
        {},
        offerConfigDefaults,
        res.offer_config ? res.offer_config : preview,
      );

      // We set consistent values for offer form preview
      const expiresOnDate = _.get(res, 'offer_config.end_date_time', null);
      const expiresOn = expiresOnDate
        ? this.$t('pos.postPreview.offer.expires-on', { date: expiresOnDate })
        : '';

      const redeemType = _.get(res, 'offer_config.redeem', null);
      const redeem = redeemType
        ? this.$t('pos.postPreview.offer.redeem', { redeem: redeemType })
        : '';

      res.expiresText = expiresOn;
      res.redeemText = redeem;

      const seperator = expiresOn ? ' • ' : '';
      res.description = `${expiresOn}${seperator}${(!this.internalMobilePreview ? redeem : '')}`;
      res.caption = this.$t('pos.postPreview.offer.be-first-to-get-offer');
      res.code_type = _.get(res, 'offer_config.code_type', null);
      res.code = _.get(res, 'offer_config.code', null);

      const offerTitle = _.get(res, 'offer_config.title', null);
      if (!_.isEmpty(offerTitle)) {
        res.name = offerTitle;
      }
    },
  },
  computed: {
    ...mapState('genericConfig', ['config']),
    defaultImage() {
      return defaults.image;
    },
    isCtaVisible() {
      return this.resultCreative.cta !== GOAL_TYPES.NO_BUTTON;
    },
    inStatus() {
      return this.creative.status === CREATIVE_STATUSES.READY ||
        this.creative.status === CREATIVE_STATUSES.APPROVED;
    },
    resultCreative() {
      const defaultValues = defaults.creative(this.goalType);

      if (this.inStatus || this.isTimeline || !this.placeholders) {
        delete defaultValues.description;
        delete defaultValues.name;
        delete defaultValues.caption;
      }

      const defaultCreativeValues = Object.assign({}, defaultValues);
      const res = _.merge(defaultCreativeValues, this.removeEmpty(this.creative));

      // Offer Settings
      if (this.goalType.isOffer) {
        this.setOfferConfigValues(res);
      }

      // Media
      res.media = [];
      if (this.creative.media && this.creative.media.length) {
        this.setMediaValues(res);
      }

      if (
        (this.placeholders && !res.media.length) ||
        (!_.isEmpty(res.media) && res.media[0].path === null)
      ) {
        const defaultMedia = defaults.media(this.goalType);

        if (this.inStatus || this.isTimeline || !this.placeholders) {
          delete defaultMedia.description;
          delete defaultMedia.name;
          delete defaultMedia.caption;
        }

        if (this.goalType.isOffer) {
          if (!_.isEmpty(res.name)) {
            defaultMedia.name = res.name;
          }
          defaultMedia.description = res.description;
        }
        res.media = this.creative.type === CAMPAIGN_TYPES.CAROUSEL ?
        [defaultMedia, defaultMedia, defaultMedia] : [defaultMedia];
      }
      return res;
    },
    resultFacebookPage() {
      const res = Object.assign({}, defaults.facebookPage(this.$t.bind(this)));
      defaults.basicFbPageKeys.forEach(i => applyKey(res, this.facebookPage, i, false));
      return res;
    },
    resultLandingPage() {
      const res = Object.assign({}, defaults.landingPage(this.$t.bind(this)));
      defaults.basicLandingPageKeys.forEach(i => {
        const landingPages = _.get(this.creative, 'campaign.landingpages.0', {});
        applyKey(res, landingPages, i, false);
      });
      return res;
    },
    icons() {
      return icons;
    },
    leadFormThumbnail() {
      const leadPhoto = _.get(this.resultCreative, 'lead_photo.thumbnail', null);
      if (leadPhoto) {
        return leadPhoto;
      }

      const firstMedia = _.get(this.resultCreative, 'media[0].thumbnail', null);
      if (firstMedia) {
        return firstMedia;
      }

      return this.defaultImage;
    },
    defaultLeadConfig() {
      return _.get(this.config, `goal_types.${GOAL_LEAD_GENERATION}.config`, null);
    },
    customQuestion() {
      const question = _.get(this.creative, 'lead_config.default_question', null);

      if (question) {
        return question;
      }

      return this.defaultLeadConfig.default_question;
    },
    goalType() {
      const cgt = _.get(this.creative, 'goal_type');
      const ccgr = _.get(this.creative, 'campaign.campaign_rule.goal_type');
      return {
        isOffer: cgt === GOAL_LOCAL_OFFERS || ccgr === GOAL_LOCAL_OFFERS,
        isLead: cgt === GOAL_LEAD_GENERATION || ccgr === GOAL_LEAD_GENERATION,
      };
    },
    currentSlide() {
      return this.counter % this.resultCreative.media.length;
    },
    getCreativePreviewCardClasses() {
      if (this.internalMobilePreview) {
        return `preview-mobile preview-type-${this.resultCreative.type}`;
      }
      return `preview-web preview-type-${this.resultCreative.type}`;
    },
    groupedMessageByLines() {
      if (this.resultCreative && this.resultCreative.message) {
        return this.resultCreative.message.split(/[\r\n]+/);
      }
      return [];
    },
  },
  mounted() {
    this.updateGenericConfig().then(() => { this.loading = false; });
  },
  updated() {
    this.$emit('creativePreviewUpdated');
  },
  watch: {
    mobilePreview: {
      immediate: true,
      handler: function setMobilePreviewWatcher() {
        this.setMobilePreview(this.mobilePreview);
      },
    },
    'resultCreative.duration_ms': {
      immediate: true,
      handler: function watchDurationMs(newDuration) {
        if (this.creative.type === CAMPAIGN_TYPES.SLIDESHOW) {
          clearTimeout(this.timer);
          const duration = newDuration >= 100 ? newDuration : 500;
          this.timer = setInterval(() => this.counter += 1, duration);
        }
      },
    },
  },
};



// WEBPACK FOOTER //
// ./src/gotu-vue-common/src/components/creative-preview/CreativePreview.js